<template>
  <v-app>
    <section class="section-edit edit-template">
      <h2>Edit User template</h2>

      <v-snackbar
        v-model="snackbar"
        color="success"
        top
        right
        :timeout=3000
      >
        <v-icon dark left>mdi-check</v-icon>
        Template has been updated. Redirection to the template page in {{ countDown }}
        <v-btn
          dark
          text
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </v-snackbar>

      <!-- LOADER -->
      <div v-if="isLoading" class="text-center">
        <v-progress-circular
          :size="50"
          :width="4"
          color="primary"
          indeterminate
          class="mt-10 mb-10"
        />
      </div>

      <!-- CONTENT -->
      <template v-else>
        <v-form>
          <!-- COMMON PANEL -->
          <v-expansion-panels
            v-model="panel"
            class="panel mt-8"
            multiple
          >
            <v-expansion-panel>
              <v-expansion-panel-header>Common</v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-text-field
                  label="Title*"
                  hide-details
                  class="input"
                  :value="template.title"
                  v-model="template.title"
                  :rules="rules"
                />
                <div class="notification-type">
                  <v-select
                    :items="notificationType"
                    item-text="iconLabel"
                    item-value="iconValue"
                    v-model="templateTypeName"
                    label="Notification icon"
                    class="mt-8 mb-3 mr-8"
                    @change="setIconUrl"
                    :rules="rules"
                  />
                  <v-img
                    v-if="templateTypeName"
                    contain
                    aspect-ratio="1"
                    max-width="60"
                    max-height="60"
                    class="notification-icon"
                    :src="setIconUrl()"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- langage PANELS -->
            <v-expansion-panel v-for="(translation, id) in template.translations" :key="id">
              <v-expansion-panel-header>{{ getLangageString(translation.langageId) }}</v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-text-field
                  label="Notification Title*"
                  class="input"
                  :value="translation.title"
                  v-model="translation.title"
                  :rules="rules"
                />
                <v-textarea
                  label="Notification Content"
                  class="input"
                  :value="translation.description"
                  v-model="translation.description"
                  :rules="rules"
                />
                <v-btn
                  @click="deleteTranslation(id)"
                  width="100"
                  color="error"
                  class="mt-3"
                  dark
                >
                  Delete
                  <v-icon dark right>mdi-trash-can</v-icon>
                </v-btn>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- NEW TRANSLATION PANEL -->
            <v-expansion-panel>
              <v-expansion-panel-header>Add new translation...</v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-radio-group v-model="langageId" :mandatory="false">
                  <v-radio :disabled="langageIdUsed.includes(1)" label="English (en)" :value=1 />
                  <v-radio :disabled="langageIdUsed.includes(2)" label="Danish (da)" :value=2 />
                  <v-radio :disabled="langageIdUsed.includes(3)" label="German (de)" :value=3 />
                  <v-radio :disabled="langageIdUsed.includes(4)" label="Spanish (es)" :value=4 />
                  <v-radio :disabled="langageIdUsed.includes(5)" label="French (fr)" :value=5 />
                  <v-radio :disabled="langageIdUsed.includes(6)" label="Hungarian (hu)" :value=6 />
                  <v-radio :disabled="langageIdUsed.includes(7)" label="Italian (it)" :value=7 />
                  <v-radio :disabled="langageIdUsed.includes(8)" label="Dutch (nl)" :value=8 />
                  <v-radio :disabled="langageIdUsed.includes(9)" label="Polish (po)" :value=9 />
                </v-radio-group>
                <v-btn
                  width="100"
                  color="primary"
                  @click="addTranslation"
                  class="mt-3"
                >
                  Add
                  <v-icon dark right>mdi-plus</v-icon>
                </v-btn>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-form>

        <v-btn
          @click="redirectToBack"
          width="250"
          class="btn mt-6"
          color="error"
        >
          <v-icon dark left size="large">mdi-arrow-left</v-icon>
          Cancel
        </v-btn>
        <v-btn
          :disabled="!isFormValid"
          @click="saveTemplate"
          width="250"
          class="btn mt-6 ml-3"
          color="primary"
        >
          Save template
          <v-icon dark right size="large">mdi-content-save</v-icon>
        </v-btn>
      </template>
    </section>
  </v-app>
</template>

<script>
  import templatesApi from "../../../services/api/templates-api";

  import notificationType from '../../../data/icons';

  export default {
    name: 'updateTemplate',
    props: ['id'],

    data() {
      return {
        panel: [0],
        template: {
          title: null,
          iconUrl: null,
          translations: []
        },
        notificationType,
        templateTypeName: null,
        langageId: null,
        langageIdUsed: [],
        snackbar: false,
        countDown: 3,
        rules: [ value => !!value || 'Required.' ],
        isFormValid: false,
        isLoading: true
      }
    },

    async mounted() {
      // Chargement du template
      let data = await templatesApi.getSingleTemplate(this.$route.params.id);

      // On récupère uniquement les données à modifier
      const iconUrl = data.data.iconUrl;
      this.template.title = data.data.title;
      this.template.iconUrl = iconUrl;  // L'URL en base est déjà l'URL complète de l'image, pas besoin de la modifier
      this.template.translations = data.data.translations;

      // On ne garde que la partie de l'URL qui correspond au nom de l'icône
      this.templateTypeName = iconUrl.substring(iconUrl.lastIndexOf('/') + 1);

      // On ajoute les langues déjà utilisés dans le tableau langageIdUsed
      for (let i = 0; i < this.template.translations.length; i++) {
        this.langageIdUsed.push(this.template.translations[i].langageId);
      }

      this.isLoading = false;
    },

    watch: {
      // On vérifie si tous les champs requis du formulaire sont renseignés
      template: {
        handler (value) {
          this.isFormValid = !!(value.title && value.iconUrl && value.translations.every((translation) => translation.title && translation.description));
        },
        deep: true,
      }
    },

    methods: {
      redirectToBack() {
        this.$router.back();
      },

      addTranslation() {
        this.isFormValid = false;

        this.template.translations.push({
          title: null,
          description: null,
          langageId: this.langageId
        });

        this.langageIdUsed.push(this.langageId);
      },

      deleteTranslation(translationId) {
        this.template.translations.splice(translationId, 1);
        this.langageIdUsed.splice(translationId, 1);
      },

      getLangageString(langageId) {
        switch (langageId) {
          case 1: return 'English (en)';
          case 2: return 'Danish (da)';
          case 3: return 'German (de)';
          case 4: return 'Spanish (es)';
          case 5: return 'French (fr)';
          case 6: return 'Hungarian (hu)';
          case 7: return 'Italian (it)';
          case 8: return 'Dutch (nl)';
          case 9: return 'Polish (po)';
          default: return 'English (en)';
        }
      },

      setIconUrl() {
        const url = `${process.env.VUE_APP_API_URL}/notification-icon/${this.templateTypeName}`;

        this.template.iconUrl = url;

        return url;
      },

      async saveTemplate() {
        await templatesApi.updateTemplate(this.template, this.$route.params.id);

        // On affiche le message indiquant que le template à bien été crée
        this.snackbar = true;
        this.countDownTimer();

        // On redirige vers le listing des templates après 3s
        setTimeout(() => {
          this.$router.push({ name: 'Templates' });
        }, 3300)
      },

      countDownTimer() {
        if (this.countDown > 0) {
          setTimeout(() => {
            this.countDown -= 1;
            this.countDownTimer();
          }, 1000)
        }
      }
    }
  };
</script>
